import { useEffect, useMemo, useRef, useState } from 'react';

export type GeolocationStatus =
  | 'idle'
  | 'unsupported'
  | 'locating'
  | 'granted'
  | 'denied'
  | 'error';

export interface GeolocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
}

export interface GeolocationState {
  status: GeolocationStatus;
  coords?: GeolocationCoordinates;
  error?: string;
  errorCode?: number;
  rawError?: {
    code: number;
    message: string;
    stack?: string;
  };
}

export interface UseGeolocationOptions {
  enabled?: boolean;
  highAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

const DEFAULT_OPTIONS: Required<UseGeolocationOptions> = {
  enabled: true,
  highAccuracy: true,
  timeout: 15_000,
  maximumAge: 300_000,
};

export const useGeolocation = (
  options: UseGeolocationOptions = DEFAULT_OPTIONS,
): GeolocationState => {
  const merged = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...options,
    }),
    [options],
  );

  const watchIdRef = useRef<number | null>(null);
  const initialStatus: GeolocationStatus = (() => {
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      return 'unsupported';
    }
    return 'idle';
  })();

  const [state, setState] = useState<GeolocationState>(() => ({
    status: initialStatus,
  }));

  useEffect(() => {
    if (!merged.enabled) {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        console.debug('[Geolocation] watchPosition cleared (disabled).');
      }
      return undefined;
    }

    if (typeof window === 'undefined' || !navigator.geolocation) {
      console.error('[Geolocation] API unsupported in this environment.');
      setState({ status: 'unsupported', error: 'Geolocation not supported' });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({
      status: prev.status === 'granted' ? prev.status : 'locating',
      coords: prev.coords,
    }));

    const handleSuccess = (position: GeolocationPosition) => {
      if (cancelled) {
        return;
      }
      const {
        coords: { latitude, longitude, accuracy },
        timestamp,
      } = position;
      setState({
        status: 'granted',
        coords: {
          latitude,
          longitude,
          accuracy,
          timestamp,
        },
        error: undefined,
        errorCode: undefined,
        rawError: undefined,
      });
    };

    const handleError = (error: GeolocationPositionError) => {
      if (cancelled) {
        return;
      }
      let status: GeolocationStatus = 'error';
      if (error.code === error.PERMISSION_DENIED) {
        status = 'denied';
      }
      setState({
        status,
        error: error.message,
        errorCode: error.code,
        rawError: {
          code: error.code,
          message: error.message,
          stack: (error as unknown as { stack?: string })?.stack,
        },
      });
      console.error('[Geolocation] watchPosition error', {
        code: error.code,
        message: error.message,
        stack: (error as unknown as { stack?: string })?.stack,
        status,
        options: merged,
      });
    };

    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleSuccess,
        handleError,
        {
          enableHighAccuracy: merged.highAccuracy,
          timeout: merged.timeout,
          maximumAge: merged.maximumAge,
        },
      );
      console.debug('[Geolocation] watchPosition started', {
        highAccuracy: merged.highAccuracy,
        timeout: merged.timeout,
        maximumAge: merged.maximumAge,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unexpected geolocation error';
      setState({ status: 'error', error: message });
      console.error('[Geolocation] watchPosition threw synchronously', {
        message,
        stack: error instanceof Error ? error.stack : undefined,
        options: merged,
      });
    }

    return () => {
      cancelled = true;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        console.debug('[Geolocation] watchPosition cleaned up.');
      }
    };
  }, [merged.enabled, merged.highAccuracy, merged.maximumAge, merged.timeout]);

  return state;
};
